import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './MultiDeviceStreamContainer.module.css';
import { FlvPlayer } from '../../components/FlvPlayer';
import { useSocket } from '../../contexts/SocketContext';
import {
  SOCKET_URL,
  STAGE_MULTI_DEVICE_READY,
  STAGE_MULTI_STAGE_DEVICES_IN_VIEW,
  STAGE_PLAYER_ENDED,
  STAGE_PUBLISHER_STARTED,
  STAGE_PUBLISHER_STOPPED,
  STAGE_RELOAD,
  STAGE_RELOAD_OVERLAY,
  STAGE_SET_MULTI_STAGE_OPTIONS,
  STREAM_ENDED,
} from '../../consts/socket';
import {
  DeviceConfig,
  LayoutOptions,
  SrcInfo,
  SrcInfoByDeviceId,
  MultiStageOptionsPayload,
  getDefaultLayoutOptions,
  prepareLayoutOptions,
  SideBySideOptions,
  PipOptions,
  MedallionOptions,
} from '../../types/multiDevice';

interface IOverlayMessage {
  elisaType: 'reload' | string;
  timestamp: number;
  payload: any;
}

// Props for memoized player container
interface PlayerContainerProps {
  deviceId: string;
  role: string;
  layout: string;
  positionStyle: React.CSSProperties;
  isDummy: boolean;
  hasValidStream: boolean;
  muted: boolean;
  srcInfo: SrcInfo | undefined;
  renderPlayer: (
    deviceId: string,
    srcInfo: SrcInfo,
    options?: { muted?: boolean },
  ) => React.ReactNode;
  renderPlaceholder: (deviceId: string, isDummy?: boolean) => React.ReactNode;
}

// Memoized player container - only re-renders when its own props change
const PlayerContainer = React.memo<PlayerContainerProps>(
  ({
    deviceId,
    role,
    layout,
    positionStyle,
    isDummy,
    hasValidStream,
    muted,
    srcInfo,
    renderPlayer,
    renderPlaceholder,
  }) => {
    return (
      <div
        className={styles.player}
        data-device-id={deviceId}
        data-role={role}
        data-layout={layout}
        data-dummy={isDummy ? 'true' : undefined}
        style={positionStyle}
      >
        {isDummy
          ? renderPlaceholder(deviceId, true)
          : hasValidStream && srcInfo
          ? renderPlayer(deviceId, srcInfo, { muted })
          : renderPlaceholder(deviceId, false)}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if relevant props changed
    return (
      prevProps.deviceId === nextProps.deviceId &&
      prevProps.role === nextProps.role &&
      prevProps.layout === nextProps.layout &&
      prevProps.isDummy === nextProps.isDummy &&
      prevProps.hasValidStream === nextProps.hasValidStream &&
      prevProps.muted === nextProps.muted &&
      prevProps.srcInfo?.srcLink === nextProps.srcInfo?.srcLink &&
      // Compare position style (shallow comparison of key properties)
      prevProps.positionStyle.opacity === nextProps.positionStyle.opacity &&
      prevProps.positionStyle.top === nextProps.positionStyle.top &&
      prevProps.positionStyle.left === nextProps.positionStyle.left &&
      prevProps.positionStyle.right === nextProps.positionStyle.right &&
      prevProps.positionStyle.bottom === nextProps.positionStyle.bottom &&
      prevProps.positionStyle.width === nextProps.positionStyle.width &&
      prevProps.positionStyle.height === nextProps.positionStyle.height &&
      prevProps.positionStyle.zIndex === nextProps.positionStyle.zIndex
    );
  },
);

function MultiDeviceStreamContainer() {
  const [searchParams] = useSearchParams();
  const overlayUrl = searchParams.get('overlayUrl') || '';

  const campaignId = searchParams.get('campaignId') || '';
  const destinationId = searchParams.get('did') || '';
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const liveCatching = !!parseInt(searchParams.get('lc') || '0');
  const liveCatchingJumpThreshold = parseFloat(
    searchParams.get('lc_jt') || '0',
  );
  const liveCatchingMaxLatency = parseFloat(searchParams.get('lc_max') || '0');
  const liveCatchingMinRemain = parseFloat(searchParams.get('lc_min') || '0');
  const useWorker = !!parseInt(searchParams.get('uw') || '0');
  const logPartialMessages = !!parseInt(searchParams.get('debug') || '0');
  const forceAllMuted = !!parseInt(searchParams.get('muted') || '0');
  const noSocket = !!parseInt(searchParams.get('noSocket') || '0');
  const orientation =
    (searchParams.get('o') || 'p') === 'p' ? 'portrait' : 'landscape';
  const showCursor = !!parseInt(searchParams.get('cursor') || '0');

  // Parse initial srcInfoByDeviceId from URL params
  const initialSrcInfoByDeviceId = useCallback((): SrcInfoByDeviceId => {
    const srcLinksParam = searchParams.get('srcLinks');
    if (srcLinksParam) {
      try {
        return JSON.parse(srcLinksParam) as SrcInfoByDeviceId;
      } catch (e) {
        console.error('Failed to parse srcLinks:', e);
      }
    }
    return {};
  }, [searchParams]);

  // Parse initial layoutOptions, defaulting to focus with first device
  const initialLayoutOptions = useCallback((): LayoutOptions => {
    const layoutOptionsParam = searchParams.get('layoutOptions');
    if (layoutOptionsParam) {
      try {
        return JSON.parse(layoutOptionsParam);
      } catch (e) {
        console.error('Failed to parse layoutOptions:', e);
      }
    }
    // Default to focus layout
    return getDefaultLayoutOptions();
  }, [searchParams]);

  const [srcInfoByDeviceId, setSrcInfoByDeviceId] = useState<SrcInfoByDeviceId>(
    initialSrcInfoByDeviceId,
  );
  const [layoutOptions, setLayoutOptions] =
    useState<LayoutOptions>(initialLayoutOptions);
  const [activeDevices, setActiveDevices] = useState<Set<string>>(
    new Set(Object.keys(initialSrcInfoByDeviceId())),
  );

  const screensaverActive = useRef<boolean>(false);
  const screensaverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const layoutOptionsRef = useRef<LayoutOptions>(initialLayoutOptions());
  const srcInfoByDeviceIdRef = useRef<SrcInfoByDeviceId>(
    initialSrcInfoByDeviceId(),
  );

  const { connect, disconnect, subscribe, emit } = useSocket();

  // Helper function to calculate video dimensions based on ratio
  const getVideoDimensions = useCallback(
    (
      srcInfo: SrcInfo | undefined,
      baseSize: number,
    ): { width: string; height: string } => {
      const ratio = srcInfo?.ratio || 16 / 9;
      const unit = orientation === 'portrait' ? 'vw' : 'vh';

      if (ratio >= 1) {
        // Landscape video
        return {
          width: `${baseSize}${unit}`,
          height: `${baseSize / ratio}${unit}`,
        };
      } else {
        // Portrait video
        return {
          width: `${baseSize * ratio}${unit}`,
          height: `${baseSize}${unit}`,
        };
      }
    },
    [orientation],
  );

  const postMessageToOverlay = useCallback(
    (elisaType: string, payload?: any) => {
      if (!iframeRef.current) {
        return;
      }

      const timestamp = new Date().getTime();

      const message = JSON.stringify({
        elisaType,
        timestamp,
        payload,
      });

      const loggableWithPayload = !['video-time-update'].includes(elisaType);

      if (loggableWithPayload) {
        console.log(`Overlay Message Sending: ${message}`);
      } else if (logPartialMessages) {
        console.log(
          `Overlay Message Sending (PARTIAL): ${JSON.stringify({
            elisaType,
            timestamp,
          })}`,
        );
      }

      iframeRef.current.contentWindow?.postMessage(JSON.parse(message), '*');
    },
    [logPartialMessages],
  );

  // Check if all layout devices are disconnected
  // Screensaver activates when none of the layout's active devices are streaming
  const areAllLayoutDevicesDisconnected = useCallback((): boolean => {
    const srcInfo = srcInfoByDeviceIdRef.current;
    const layoutOpts = layoutOptionsRef.current;

    // Get active device IDs from layout config
    const layoutDeviceIds = (layoutOpts.devices || [])
      .filter((d: DeviceConfig) => d.active)
      .map((d: DeviceConfig) => d.deviceId);

    // Get streaming device IDs (have valid srcLink and not dummy)
    const streamingDeviceIds = Object.keys(srcInfo).filter((deviceId) => {
      const info = srcInfo[deviceId];
      return (
        info && info.srcLink && info.srcLink.trim() !== '' && !info.isDummy
      );
    });

    // Check if any layout device is streaming
    const streamingLayoutDevices = layoutDeviceIds.filter((deviceId: string) =>
      streamingDeviceIds.includes(deviceId),
    );

    console.log(
      'Screensaver check:',
      JSON.stringify({
        layoutDeviceIds,
        streamingDeviceIds,
        streamingLayoutDevices,
      }),
    );

    if (streamingLayoutDevices.length === 0) {
      console.log('No layout devices are streaming, activating screensaver');
      return true;
    }

    console.log(
      `${streamingLayoutDevices.length} layout device(s) still streaming, NOT activating screensaver`,
    );
    return false;
  }, []);

  // Cancel any pending screensaver timeout
  const cancelScreensaverTimeout = useCallback(() => {
    if (screensaverTimeoutRef.current) {
      clearTimeout(screensaverTimeoutRef.current);
      screensaverTimeoutRef.current = null;
      console.log('Screensaver timeout cancelled.');
    }
  }, []);

  // Schedule screensaver check
  const scheduleScreensaverCheck = useCallback(() => {
    cancelScreensaverTimeout();

    screensaverTimeoutRef.current = setTimeout(() => {
      if (areAllLayoutDevicesDisconnected()) {
        console.log('All layout devices disconnected. Activating screensaver.');
        postMessageToOverlay('screensaver_on', { orientation });
        screensaverActive.current = true;
      } else {
        console.log(
          'Some layout devices still streaming. Screensaver not activated.',
        );
      }
    }, 3000);

    console.log('Screensaver check scheduled in 3s.');
  }, [
    areAllLayoutDevicesDisconnected,
    cancelScreensaverTimeout,
    orientation,
    postMessageToOverlay,
  ]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    if (!noSocket) {
      connect({
        url: SOCKET_URL,
        query: { campaignId, userUid: 'studio' },
        connectCallback: addSocketEventHandlers,
      });
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      if (!noSocket) {
        disconnect();
      }
      cancelScreensaverTimeout();
    };
  }, []);

  // Helper to update srcInfoByDeviceId with delta updates to preserve unchanged references
  const updateSrcInfoByDeviceId = useCallback(
    (newSrcInfo: SrcInfoByDeviceId) => {
      setSrcInfoByDeviceId((prev) => {
        const newDeviceIds = Object.keys(newSrcInfo);
        const prevDeviceIds = Object.keys(prev);

        // Check which devices actually changed
        let hasAnyChanges = false;
        const updated: SrcInfoByDeviceId = {};

        // Process new/updated devices
        for (const deviceId of newDeviceIds) {
          const newInfo = newSrcInfo[deviceId];
          const prevInfo = prev[deviceId];

          if (
            !prevInfo ||
            newInfo.srcLink !== prevInfo.srcLink ||
            newInfo.isDummy !== prevInfo.isDummy
          ) {
            // Device is new or changed - use new reference
            updated[deviceId] = newInfo;
            hasAnyChanges = true;
          } else {
            // Device unchanged - keep same reference
            updated[deviceId] = prevInfo;
          }
        }

        // Check if any devices were removed
        for (const deviceId of prevDeviceIds) {
          if (!(deviceId in newSrcInfo)) {
            hasAnyChanges = true;
            // Device removed - don't include in updated
          }
        }

        if (!hasAnyChanges) {
          return prev; // No state update, no re-render
        }

        srcInfoByDeviceIdRef.current = updated;
        setActiveDevices(new Set(newDeviceIds));
        return updated;
      });
    },
    [],
  );

  // Helper to apply multi-stage options from any source (socket or postMessage)
  const applyMultiStageOptions = useCallback(
    (data: MultiStageOptionsPayload) => {
      console.log('Applying multi-stage options:', JSON.stringify(data));

      if (data.layoutOptions) {
        let layoutOptions = data.layoutOptions;

        // Focus layout shows exactly one device; if the payload doesn't mark
        // one active, default to the first streaming device so the canvas
        // isn't blank.
        const hasActiveDevice = (layoutOptions.devices ?? []).some(
          (d) => d.active,
        );
        if (layoutOptions.layout === 'focus' && !hasActiveDevice) {
          const srcInfo =
            data.srcInfoByDeviceId ?? srcInfoByDeviceIdRef.current;
          const firstDeviceId = Object.keys(srcInfo)[0];
          if (firstDeviceId) {
            const existing = layoutOptions.devices ?? [];
            const idx = existing.findIndex((d) => d.deviceId === firstDeviceId);
            const devices =
              idx >= 0
                ? existing.map((d, i) =>
                    i === idx ? { ...d, active: true } : d,
                  )
                : [...existing, { deviceId: firstDeviceId, active: true }];
            layoutOptions = { ...layoutOptions, devices };
          }
        }

        layoutOptionsRef.current = layoutOptions;
        setLayoutOptions(layoutOptions);
      }

      if (data.srcInfoByDeviceId) {
        updateSrcInfoByDeviceId(data.srcInfoByDeviceId);
      }

      // Check if screensaver should be turned off
      if (screensaverActive.current && !areAllLayoutDevicesDisconnected()) {
        console.log(
          'Devices in layout are now streaming. Turning off screensaver.',
        );
        postMessageToOverlay('screensaver_off', { orientation });
        screensaverActive.current = false;
      }
    },
    [
      updateSrcInfoByDeviceId,
      areAllLayoutDevicesDisconnected,
      postMessageToOverlay,
      orientation,
    ],
  );

  const handleMessage = (e: MessageEvent<IOverlayMessage>) => {
    const origin = e.origin;

    // Allow messages from any origin when embedded in iframe (for preview mode)
    // But still validate the message structure
    if (!e.data || typeof e.data !== 'object') {
      return;
    }

    // For overlay messages, check origin
    const isOverlayMessage = ['after-show-end', 'reload'].includes(
      e.data.elisaType,
    );

    if (isOverlayMessage) {
      try {
        const urlObj = new URL(origin);
        const hostname = urlObj.hostname;
        if (
          hostname !== 'elisa-overlay.onrender.com' &&
          !hostname.includes('localhost') &&
          !hostname.includes('ngrok')
        ) {
          return;
        }
      } catch {
        return;
      }
    }

    console.log(`PostMessage Received: ${JSON.stringify(e.data)}`);

    switch (e.data.elisaType) {
      case 'after-show-end':
        console.log('AFTER SHOW END FROM MESSAGE EVENT');
        if (!noSocket) {
          emit(STREAM_ENDED, { forceEnd: true });
        }
        break;
      case 'reload':
        console.log('RELOAD FROM MESSAGE EVENT');
        window.location.reload();
        break;
      case 'setMultiStageOptions':
        // Handle configuration from parent iframe (preview mode)
        console.log('SET MULTI STAGE OPTIONS FROM POSTMESSAGE');
        if (e.data.payload) {
          applyMultiStageOptions(e.data.payload as MultiStageOptionsPayload);
        }
        break;
    }
  };

  const addSocketEventHandlers = () => {
    subscribe(
      STAGE_PUBLISHER_STARTED,
      (data: { deviceId: string; srcInfoByDeviceId: SrcInfoByDeviceId }) => {
        const deviceId = data?.deviceId || '0';
        console.log(
          `Socket Event Received: ${STAGE_PUBLISHER_STARTED} for device ${deviceId}`,
        );

        // Update srcInfoByDeviceId with change detection
        updateSrcInfoByDeviceId(data.srcInfoByDeviceId);
        console.log(`Streaming devices:`, Object.keys(data.srcInfoByDeviceId));

        if (screensaverActive.current) {
          console.log(`RELOAD AS STREAM RESUMED FOR DEVICE ${deviceId}.`);
          window.location.reload();
          return;
        }

        // Cancel screensaver check if a device reconnects
        if (screensaverTimeoutRef.current) {
          console.log(
            `Device ${deviceId} connected. Cancelling screensaver check.`,
          );
          cancelScreensaverTimeout();
        }
      },
    );

    subscribe(
      STAGE_PUBLISHER_STOPPED,
      (data: { deviceId: string; srcInfoByDeviceId: SrcInfoByDeviceId }) => {
        const deviceId = data?.deviceId || '0';
        console.log(
          `Socket Event Received: ${STAGE_PUBLISHER_STOPPED} for device ${deviceId}`,
        );

        // Update srcInfoByDeviceId with change detection
        updateSrcInfoByDeviceId(data.srcInfoByDeviceId);
        console.log(
          `Remaining streaming devices:`,
          JSON.stringify(Object.keys(data.srcInfoByDeviceId)),
        );

        // Schedule screensaver check (will only activate if no devices are streaming)
        scheduleScreensaverCheck();
      },
    );

    subscribe(
      STAGE_SET_MULTI_STAGE_OPTIONS,
      (data: MultiStageOptionsPayload) => {
        console.log(
          `Socket Event Received: ${STAGE_SET_MULTI_STAGE_OPTIONS}`,
          JSON.stringify(data),
        );
        applyMultiStageOptions(data);
      },
    );

    subscribe(STAGE_RELOAD, () => {
      console.log(`Socket Event Received: ${STAGE_RELOAD}`);
      console.log('RELOAD FROM SOCKET EVENT');
      window.location.reload();
    });

    subscribe(STAGE_RELOAD_OVERLAY, () => {
      console.log(`Socket Event Received: ${STAGE_RELOAD_OVERLAY}`);
      console.log('RELOAD OVERLAY ONLY FROM SOCKET EVENT');
      iframeRef.current?.contentWindow?.location?.reload();
    });

    console.log(`Socket Event Subscriptions Made for Multi-Device Stage`);

    // Emit ready event to request fresh configuration from coordinator
    console.log('Emitting multiDeviceStageReady event...');
    emit(STAGE_MULTI_DEVICE_READY, { campaignId });
  };

  const onEnded = useCallback(
    (deviceId: string) => {
      if (!noSocket) {
        emit(STAGE_PLAYER_ENDED, { destinationId, deviceId });
      }
    },
    [destinationId, emit, noSocket],
  );

  // Infer srcLinkType from URL if not provided
  const inferSrcLinkType = useCallback((srcLink: string): string => {
    if (!srcLink) {
      return 'flv';
    }

    try {
      const urlObj = new URL(srcLink);
      const path = urlObj.pathname;

      if (path.indexOf('.') === -1) {
        return 'flv';
      }

      return path.split('.').pop()?.toLowerCase() || 'flv';
    } catch {
      return 'flv';
    }
  }, []);

  // Render individual player - memoized to prevent PlayerContainer re-renders
  const renderPlayerFn = useCallback(
    (
      deviceId: string,
      srcInfo: SrcInfo,
      options?: { muted?: boolean },
    ): React.ReactNode => {
      const srcType = srcInfo.srcLinkType || inferSrcLinkType(srcInfo.srcLink);

      return (
        <FlvPlayer
          key={deviceId}
          isPreRecorded={false}
          liveCatching={liveCatching}
          liveCatchingJumpThreshold={liveCatchingJumpThreshold}
          liveCatchingMaxLatency={liveCatchingMaxLatency}
          liveCatchingMinRemain={liveCatchingMinRemain}
          muted={options?.muted ?? false}
          onVideoEnded={() => onEnded(deviceId)}
          onVideoTimeUpdated={undefined}
          src={srcInfo.srcLink}
          srcType={srcType}
          useWorker={useWorker}
        />
      );
    },
    [
      inferSrcLinkType,
      liveCatching,
      liveCatchingJumpThreshold,
      liveCatchingMaxLatency,
      liveCatchingMinRemain,
      onEnded,
      useWorker,
    ],
  );

  // Color palette for dummy device placeholders (solid colors for CPU efficiency)
  const dummyColors = useMemo(
    () => [
      '#2d3436', // Gray
      '#6c5ce7', // Purple
      '#00b894', // Green
      '#e17055', // Orange
      '#0984e3', // Blue
      '#f39c12', // Yellow
      '#d63031', // Red
      '#00cec9', // Cyan
      '#e84393', // Pink
      '#273c75', // Navy
      '#8e44ad', // Violet
      '#1abc9c', // Teal
    ],
    [],
  );

  // Get consistent color for a device based on its ID - memoized for stable reference
  const getPlaceholderColor = useCallback(
    (deviceId: string, isDummy: boolean): string => {
      if (!isDummy) {
        return '#1a1a2e'; // Default dark
      }
      // Improved hash function for better distribution (djb2 algorithm)
      let hash = 5381;
      for (let i = 0; i < deviceId.length; i++) {
        hash = (hash * 33) ^ deviceId.charCodeAt(i);
      }
      return dummyColors[Math.abs(hash) % dummyColors.length];
    },
    [dummyColors],
  );

  // Render placeholder for missing/dummy device - memoized to prevent PlayerContainer re-renders
  const renderPlaceholderFn = useCallback(
    (deviceId: string, isDummy = false): React.ReactNode => {
      const backgroundColor = getPlaceholderColor(deviceId, isDummy);

      return (
        <div
          className={styles.placeholder}
          key={`placeholder-${deviceId}`}
          style={{ background: backgroundColor }}
        >
          <div style={{ textAlign: 'center' }}>
            <svg
              className={styles.placeholderIcon}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
            </svg>
            {isDummy ? (
              <>
                <div style={{ fontSize: '16px', fontWeight: 500 }}>
                  {deviceId}
                </div>
                <div
                  style={{ marginTop: '4px', fontSize: '12px', opacity: 0.7 }}
                >
                  No stream
                </div>
              </>
            ) : (
              <div>Waiting for device...</div>
            )}
          </div>
        </div>
      );
    },
    [getPlaceholderColor],
  );

  // Prepare layout options: filter active devices, apply maxDevices, set defaults
  // Memoized to prevent recalculation on srcInfoByDeviceId changes
  const preparedLayoutOptions = useMemo(
    () => prepareLayoutOptions(layoutOptions),
    [layoutOptions],
  );

  // Get device role based on layout and position in devices array
  // Get device role based purely on layout config position (not online status)
  // Expects preparedLayoutOptions (already filtered by active and sliced by maxDevices)
  const getDeviceRole = useCallback(
    (deviceId: string, opts: LayoutOptions): string => {
      const layoutDevices = (opts.devices || []).map(
        (d: DeviceConfig) => d.deviceId,
      );

      const index = layoutDevices.indexOf(deviceId);
      if (index === -1) return 'hidden'; // Device not in layout

      if (opts.layout === 'sideBySide') return `grid-${index}`;
      if (index === 0) return 'primary';
      return `secondary-${index - 1}`;
    },
    [],
  );

  // Get position style for a device based on layout config (not online status)
  // Expects preparedLayoutOptions (already filtered by active and sliced by maxDevices)
  // deviceInfoMap is only used for dimension calculations, not for filtering
  const getPositionStyle = useCallback(
    (
      deviceId: string,
      opts: LayoutOptions,
      deviceInfoMap: SrcInfoByDeviceId,
    ): React.CSSProperties => {
      const layoutDevices = (opts.devices || []).map(
        (d: DeviceConfig) => d.deviceId,
      );
      const index = layoutDevices.indexOf(deviceId);

      // Hidden players: keep fullscreen but invisible, video keeps playing
      // This prevents rebuffering when switching focus/layouts
      if (index === -1) {
        return {
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 0,
        };
      }

      const edgeOffset = 20;

      if (opts.layout === 'focus') {
        // Only the first device in the layout is visible, others are hidden
        if (index !== 0) {
          return {
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: 0,
          };
        }
        return {
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
        };
      }

      if (opts.layout === 'sideBySide') {
        const sbsOpts = opts as SideBySideOptions;
        const gap = sbsOpts.gap ?? 4;

        // Explicit grid configuration (defaults: 2 columns, 1 row)
        const columns = sbsOpts.columns ?? 2;
        const rows = sbsOpts.rows ?? 1;

        const col = index % columns;
        const row = Math.floor(index / columns);

        // Total gap space between cells (not at edges)
        const totalHorizontalGap = (columns - 1) * gap;
        const totalVerticalGap = (rows - 1) * gap;

        // Cell dimensions: (100% - total gaps) / number of cells
        // Using calc() to properly mix percentages and pixels
        const cellWidth =
          columns === 1
            ? '100%'
            : `calc((100% - ${totalHorizontalGap}px) / ${columns})`;
        const cellHeight =
          rows === 1
            ? '100%'
            : `calc((100% - ${totalVerticalGap}px) / ${rows})`;

        // Position: cell index * (cell size + gap)
        const colGapOffset = col * gap;
        const rowGapOffset = row * gap;
        const cellLeft =
          col === 0
            ? '0'
            : `calc(${col} * (100% - ${totalHorizontalGap}px) / ${columns} + ${colGapOffset}px)`;
        const cellTop =
          row === 0
            ? '0'
            : `calc(${row} * (100% - ${totalVerticalGap}px) / ${rows} + ${rowGapOffset}px)`;

        return {
          top: cellTop,
          left: cellLeft,
          width: cellWidth,
          height: cellHeight,
          zIndex: 1,
        };
      }

      if (opts.layout === 'pip') {
        const pipOpts = opts as PipOptions;
        const position = pipOpts.position || 'bottomRight';
        const pipStack = pipOpts.pipStack || 'vertical';
        const pipSpacing = pipOpts.pipSpacing ?? 10;
        const secondarySize = pipOpts.secondarySize || 25;
        const pipBorderColor = pipOpts.pipBorderColor;
        const pipBorderSize = pipOpts.pipBorderSize ?? 0;

        if (index === 0) {
          // Primary - fullscreen
          return {
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
          };
        }

        // Secondary PIPs
        const secondaryIndex = index - 1;
        const srcInfo = deviceInfoMap[deviceId];
        const dims = getVideoDimensions(srcInfo, secondarySize);

        const isRight = position.includes('Right');
        const isBottom = position.includes('bottom');

        // Build CSS calc() offset string using previous PIP dimensions
        // This avoids mixing px with vw/vh units
        const buildOffsetStr = (): string => {
          const parts: string[] = [`${edgeOffset}px`];
          for (let i = 1; i < index; i++) {
            const prevDeviceId = layoutDevices[i];
            const prevSrcInfo = deviceInfoMap[prevDeviceId];
            const prevDims = getVideoDimensions(prevSrcInfo, secondarySize);
            const prevSize =
              pipStack === 'horizontal' ? prevDims.width : prevDims.height;
            parts.push(prevSize);
            parts.push(`${pipSpacing}px`);
          }
          return parts.length === 1 ? parts[0] : `calc(${parts.join(' + ')})`;
        };

        const offsetStr = buildOffsetStr();

        const style: React.CSSProperties = {
          width: dims.width,
          height: dims.height,
          zIndex: 5 + secondaryIndex,
          borderRadius: '8px',
          overflow: 'hidden',
          border:
            pipBorderSize > 0
              ? `${pipBorderSize}px solid ${
                  pipBorderColor || 'rgba(255, 255, 255, 0.8)'
                }`
              : undefined,
        };

        if (pipStack === 'horizontal') {
          style.top = isBottom ? undefined : `${edgeOffset}px`;
          style.bottom = isBottom ? `${edgeOffset}px` : undefined;
          style.right = isRight ? offsetStr : undefined;
          style.left = isRight ? undefined : offsetStr;
        } else {
          style.top = isBottom ? undefined : offsetStr;
          style.bottom = isBottom ? offsetStr : undefined;
          style.right = isRight ? `${edgeOffset}px` : undefined;
          style.left = isRight ? undefined : `${edgeOffset}px`;
        }

        return style;
      }

      if (opts.layout === 'medallion') {
        const medOpts = opts as MedallionOptions;
        const medallionPosition = medOpts.medallionPosition || 'topRight';
        const medallionStack = medOpts.medallionStack || 'vertical';
        const medallionSpacing = medOpts.medallionSpacing ?? 15;
        const medallionSize = medOpts.medallionSize || 20;
        const medallionBorderColor =
          medOpts.medallionBorderColor ?? 'rgba(255, 255, 255, 0.8)';
        const medallionBorderSize = medOpts.medallionBorderSize ?? 3;

        if (index === 0) {
          // Primary - fullscreen
          return {
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
          };
        }

        // Secondary medallions
        const secondaryIndex = index - 1;
        const unit = orientation === 'portrait' ? 'vw' : 'vh';
        const medallionDimension = `${medallionSize}${unit}`;

        const isRight = medallionPosition.includes('Right');
        const isBottom = medallionPosition.includes('bottom');

        const offsetStr = `calc(${edgeOffset}px + ${secondaryIndex} * (${medallionDimension} + ${medallionSpacing}px))`;

        const style: React.CSSProperties = {
          width: medallionDimension,
          height: medallionDimension,
          zIndex: 5 + secondaryIndex,
          borderRadius: '50%',
          overflow: 'hidden',
          border: `${medallionBorderSize}px solid ${medallionBorderColor}`,
        };

        if (medallionStack === 'horizontal') {
          style.top = isBottom ? undefined : `${edgeOffset}px`;
          style.bottom = isBottom ? `${edgeOffset}px` : undefined;
          style.right = isRight ? offsetStr : undefined;
          style.left = isRight ? undefined : offsetStr;
        } else {
          style.top = isBottom ? undefined : offsetStr;
          style.bottom = isBottom ? offsetStr : undefined;
          style.right = isRight ? `${edgeOffset}px` : undefined;
          style.left = isRight ? undefined : `${edgeOffset}px`;
        }

        return style;
      }

      return {};
    },
    [getVideoDimensions, orientation],
  );

  // Render all players in a flat pool with stable keys
  // Render layout devices (visible) + all other streaming devices (hidden but buffered)
  // Uses memoized PlayerContainer to prevent re-renders when unrelated devices change
  const renderPlayerPool = () => {
    // Get device IDs from prepared layout (already filtered by active and sliced by maxDevices)
    const layoutDevices = (preparedLayoutOptions.devices || []).map(
      (d: DeviceConfig) => d.deviceId,
    );

    // Get all streaming device IDs (valid srcLink, not dummy)
    const streamingDeviceIds = Object.keys(srcInfoByDeviceId).filter(
      (deviceId) => {
        const info = srcInfoByDeviceId[deviceId];
        return (
          info && info.srcLink && info.srcLink.trim() !== '' && !info.isDummy
        );
      },
    );

    // Combine: layout devices first (in order), then any streaming devices not in layout
    // This keeps non-layout streaming players alive (hidden) to prevent audio loss
    const allDevicesToRender = [
      ...layoutDevices,
      ...streamingDeviceIds.filter((id) => !layoutDevices.includes(id)),
    ];

    return allDevicesToRender.map((deviceId: string) => {
      const role = getDeviceRole(deviceId, preparedLayoutOptions);
      const positionStyle = getPositionStyle(
        deviceId,
        preparedLayoutOptions,
        srcInfoByDeviceId,
      );
      const info = srcInfoByDeviceId[deviceId];

      // Determine what to render for this device
      const isDummy = info?.isDummy === true;
      const hasValidStream = Boolean(
        info && info.srcLink && info.srcLink.trim() !== '' && !isDummy,
      );

      // Muted state is sticky - only use forceAllMuted from URL param
      // Do NOT mute based on visibility, as hidden players may still provide audio

      return (
        <PlayerContainer
          key={deviceId}
          deviceId={deviceId}
          role={role}
          layout={preparedLayoutOptions.layout}
          positionStyle={positionStyle}
          isDummy={isDummy}
          hasValidStream={hasValidStream}
          muted={forceAllMuted}
          srcInfo={info}
          renderPlayer={renderPlayerFn}
          renderPlaceholder={renderPlaceholderFn}
        />
      );
    });
  };

  // Show placeholder when no devices in layout have active streams
  const renderEmptyState = () => {
    // Get device IDs from prepared layout (already filtered by active and sliced by maxDevices)
    const layoutDeviceIds = (preparedLayoutOptions.devices || []).map(
      (d: DeviceConfig) => d.deviceId,
    );

    // Check if any device in the layout has an active stream
    const hasActiveStream = layoutDeviceIds.some((deviceId: string) => {
      const info = srcInfoByDeviceId[deviceId];
      return (
        info && info.srcLink && info.srcLink.trim() !== '' && !info.isDummy
      );
    });

    if (layoutDeviceIds.length > 0 && hasActiveStream) return null;

    return (
      <div className={styles.focusContainer}>
        <div className={styles.focusPrimary}>{renderPlaceholderFn('none')}</div>
      </div>
    );
  };

  // Get wrapper style (for gap color in side-by-side layout)
  const getWrapperStyle = (): React.CSSProperties | undefined => {
    if (preparedLayoutOptions.layout === 'sideBySide') {
      const sbsOpts = preparedLayoutOptions as SideBySideOptions;
      if (sbsOpts.gapColor) {
        return { backgroundColor: sbsOpts.gapColor };
      }
    }
    return undefined;
  };

  // Emit socket event when visible devices change
  useEffect(() => {
    if (noSocket) return;

    // Get device IDs that are actually visible (in layout and not hidden)
    const visibleDeviceIds = (preparedLayoutOptions.devices || [])
      .map((d: DeviceConfig) => d.deviceId)
      .filter((deviceId: string) => {
        const role = getDeviceRole(deviceId, preparedLayoutOptions);
        const positionStyle = getPositionStyle(
          deviceId,
          preparedLayoutOptions,
          srcInfoByDeviceId,
        );
        // Device is visible if not hidden and has opacity > 0
        return role !== 'hidden' && positionStyle.opacity !== 0;
      });

    // Emit the event with visible devices and timestamp
    emit(STAGE_MULTI_STAGE_DEVICES_IN_VIEW, {
      campaignId,
      deviceIds: visibleDeviceIds,
      layout: preparedLayoutOptions.layout,
      timestamp: Date.now(),
    });

    console.log(
      'Emitting multiStageDevicesInView:',
      JSON.stringify(visibleDeviceIds),
    );
  }, [
    preparedLayoutOptions,
    srcInfoByDeviceId,
    campaignId,
    emit,
    getDeviceRole,
    getPositionStyle,
    noSocket,
  ]);

  return (
    <div
      className={styles.wrapper}
      data-layout={preparedLayoutOptions.layout}
      style={getWrapperStyle()}
    >
      {renderEmptyState()}
      {renderPlayerPool()}
      {overlayUrl && (
        <iframe
          ref={iframeRef}
          title="video-overlay"
          src={overlayUrl}
          className={styles.iframe}
        />
      )}
      {!showCursor && <div className={styles.nonCursor} />}
    </div>
  );
}

export default MultiDeviceStreamContainer;
